import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AsyncPipe } from '@angular/common';
import { AppleDevice } from '@era/locations-sdk';
import {
  AppleDeviceStoreActions,
  AppleProfilesStoreActions,
  CategoryApplicationsStoreSelectors,
  CategoryStoreSelectors,
  DeviceStoreSelectors,
} from '@era/stores';
import { AppleApplicationLockChanged } from '../../../devices/models/apple-application-lock-changed.model';
import { AppleDeviceComponent } from './apple-device.component';
import { AppleDeviceService } from './apple-device.service';

@Component({
  selector: 'era-apple-device',
  templateUrl: './apple-device.container.html',
  styleUrls: ['./apple-device.container.scss'],
  imports: [AppleDeviceComponent, AsyncPipe],
})
export class AppleDeviceContainerComponent implements OnChanges {
  @Input() udid: string;
  public readonly appleDevice$: Observable<AppleDevice>;

  private readonly udid$ = new ReplaySubject<string>(1);

  constructor(
    private readonly store: Store<AppleDevice>,
    private readonly appleDeviceService: AppleDeviceService
  ) {
    const device$ = this.udid$.pipe(
      switchMap((udid) => this.store.select(DeviceStoreSelectors.selectDeviceByUdid(udid)))
    );
    const category$ = device$.pipe(
      switchMap((device) => this.store.select(CategoryStoreSelectors.selectCategoryByDevice(device)))
    );

    this.appleDevice$ = combineLatest([category$, device$]).pipe(
      switchMap(([category, device]) =>
        this.store.select(
          CategoryApplicationsStoreSelectors.selectApplicationsForDeviceWithConfiguration(category, device)
        )
      ),
      filter((device) => device !== undefined),
      switchMap((device) => this.appleDeviceService.observeAppleDeviceWithLockedFlag(device))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.udid) {
      if (this.udid) {
        this.store.dispatch(
          AppleDeviceStoreActions.AppleDeviceContainerActions.appleDeviceRequested({ udid: this.udid })
        );
        this.store.dispatch(
          AppleProfilesStoreActions.AppleDeviceContainerActions.profilesRequested({ udid: this.udid })
        );
      }
      this.udid$.next(this.udid);
    }
  }

  public applicationLockChanged(event: AppleApplicationLockChanged) {
    this.store.dispatch(
      AppleProfilesStoreActions.AppleDeviceContainerActions.singleAppModeChanged({
        udid: event.udid,
        bundleIdentifier: event.bundleIdentifier,
        locked: event.locked,
      })
    );
  }
}
